import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { AlertNotification, Button, colors, Row, Col, Input, LabeledIconInput } from "@commonsku/styles";

import {
  InvoiceCreditMemo
} from "../../types/credit_memo";

import useDraft from "./useDraft";
import FormPopup from "../FormPopup";
import { formatMoneyCurrency, formatPercent, getCurrencySymbol } from "../../utils";

const CLIENT_CREDIT_MEMO_POPUP_WIDTH = "640px";
const CLIENT_CREDIT_MEMO_POPUP_HEIGHT = "324px";
const CREDIT_MEMO_NOTE_PLACEHOLDER_TEXT = "Add a note (optional)";

const InvoiceDetails = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.primary1['65']};
  border-radius: 8px;
  background: ${colors.white};
`;

const InvoiceDetailsHeader = styled.div`
&& {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 2rem;
  height: 3.5rem;
  padding: 0 0.5rem;
  background: ${colors.neutrals.bg1};
  border-radius: 8px 8px 0 0;
  line-height: 1.5rem;
  font-size: 1rem;
}
`;
const CreditMemoItemHeader = styled.div`
&& {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 1rem;
  padding: 0 4rem;
  background: ${colors.neutrals.bg1};
  font-size: 14px;
  font-weight: 600;
  line-height: 1rem;
  height: 3.5rem;
}
`;

const CreditMemoItemRow = styled.div`
&& {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 1rem;
  margin: 0 4rem;
  height: 3.5rem;
}
`;

const Client = styled.a`
  font-weight: 700;
`;

const Invoice = styled.span`
  font-weight: 700;
`;

const Project = styled.span`
  font-weight: 400;
`;

const CodeCell = styled.div`
&&& {
  display: flex;
  align-items: center;
  flex: 2 2 16%;
  text-align: left;
  padding-left: 1rem;
  height: 100%;
}
`;

const NameCell = styled.div`
  flex: 3 3 24%;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const InvoicedQtyCell = styled.div`
  flex: 1 1 8%;
  text-align: center;
`;

const QuantityCell = styled.div`
  flex: 1 1 8%;
  text-align: center;
`;

const UnitPriceCell = styled.div`
  flex: 1 1 8%;
  text-align: right;
`;

const SubtotalCell = styled.div`
  flex: 1 1 8%;
  text-align: right;
`;

const TaxCell = styled.div`
  flex: 2 2 16%;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const TotalCell = styled.div`
&&& {
  flex: 1 1 8%;
  text-align: right;
  padding-right: 1rem;
}`;

const QuantityInput = styled(Input)`
&&& {
  text-align: center;
  margin: 0;
}
`;

const CostInput = styled(LabeledIconInput)`
&&& {
  text-align: center;
  margin: 0;
  width: 100%;
}
`;

const getSubtotal = (item) => item.unitPrice * Number(item.quantity);
const getTotal = (item) => {
  const subtotal = getSubtotal(item);
  return subtotal + item.taxes.reduce((taxes, t) => taxes + Math.round(subtotal * t.percent) / 100, 0);
};

const getTaxes = (item) => Array.from(
  new Set(
    item.taxes.map(tax => `${tax.label} ${formatPercent(tax.percent)}`)
  )
).sort();

const getProductInvoicedQuantity = (item) => item.breakdowns.reduce((t, b) => t + Number(b.invoicedQuantity), 0);
const getProductQuantity = (item) => item.breakdowns.reduce((t, b) => t + Number(b.quantity), 0);
const getProductSubtotal = (item) => item.breakdowns.reduce((t, b) => t + getSubtotal(b), 0) + item.costs.reduce((t, c) => t + getSubtotal(c), 0);
const getProductTotal = (item) => item.breakdowns.reduce((t, b) => t + getTotal(b), 0) + item.costs.reduce((t, c) => t + getTotal(c), 0);
const getProductTaxes = (item) => Array.from(
  new Set(
    item.breakdowns.concat(item.costs).reduce(
      (t, b) => t.concat(getTaxes(b)),
      []
    )
  )
).sort();

export default function InvoiceCreditMemoPopup(
  {
    creditMemo,
    onClose,
    isDisabled = false,
  }: {
    creditMemo: InvoiceCreditMemo,
    onClose: (saved: boolean) => void,
    isDisabled?: boolean
  }
) {
  const { draft, isNew, isValid, isEditable, updater, save } = useDraft(creditMemo);
  const disabled = isDisabled || !isEditable;
  const [saved, setSaved] = useState(false);
  if ("INVOICE" !== draft.type) {
    throw new Error("Invalid credit memo");
  }

  const handleSave = useCallback(
    async () => {
      try {
        await save();
        setSaved(true);
      } catch (e) {
        toast.error(
          isNew ?
            "Unable to create credit memo" :
            "Unable to save credit memo"
        );
      }
    },
    [isNew, save]
  );

  const handleClose = useCallback(
    () => onClose(saved),
    [onClose, saved]
  );

  const title = isNew ? 'Create Credit Memo' : `Credit Memo ${creditMemo.number}`;

  if (saved) {
    return (
      <FormPopup
        isNew={isNew}
        isEditable={false}
        isValid={isValid}
        onClose={handleClose}
        title={title}
        width={CLIENT_CREDIT_MEMO_POPUP_WIDTH}
        height={CLIENT_CREDIT_MEMO_POPUP_HEIGHT}
        customActions={<>
          <Button as="a" secondary href="/finance/credit-memos">
            View Credit Memos
          </Button>
          <Button onClick={handleClose}>
            Close
          </Button>
        </>}
      >
        <AlertNotification alertType="success">
          {isNew ? 'Credit memo created' : 'Credit memo updated'}
        </AlertNotification>
        <span>You can find your credit memos in the <b>Credit Memos</b> tab of Invoicing</span>
      </FormPopup>
    );
  }

  return (
    <FormPopup
      title={title}
      onClose={handleClose}
      onSave={handleSave}
      isNew={isNew}
      isValid={isValid}
      isEditable={!disabled && creditMemo.items.length > 0}
    >
      <Row>
        <Col>
          <textarea
            style={{ minHeight: "4.5rem", margin: 0 }}
            value={draft.notes}
            onChange={(e) => updater.setNotes(e.target.value)}
            onBlur={(e) => updater.setNotes(draft.notes.trim())}
            placeholder={CREDIT_MEMO_NOTE_PLACEHOLDER_TEXT}
            disabled={disabled}
          />
        </Col>
      </Row>
      <InvoiceDetails>
        <InvoiceDetailsHeader>
          <Client href={`/client/${creditMemo.client.id}`} target="_blank">
            {creditMemo.client.name}
          </Client>
          <Invoice>
            Invoice&nbsp;
            <a
              href={`/project/${creditMemo.project.number}/invoice/${creditMemo.invoice.number}`}
              target="_blank" rel="noreferrer"
            >
              #{creditMemo.invoice.number}
            </a>
          </Invoice>
          <Project>
            Project&nbsp;
            <a
              href={`/project/${creditMemo.project.number}`}
              target="_blank" rel="noreferrer"
            >
              #{creditMemo.project.number}
            </a>
          </Project>
        </InvoiceDetailsHeader>
        <CreditMemoItemHeader>
          <CodeCell>Code</CodeCell>
          <NameCell>Item Name</NameCell>
          <InvoicedQtyCell>Invoiced<br />QTY</InvoicedQtyCell>
          <QuantityCell>Credit<br />QTY</QuantityCell>
          <UnitPriceCell>Unit Price</UnitPriceCell>
          <SubtotalCell>Subtotal</SubtotalCell>
          <TaxCell>Tax</TaxCell>
          <TotalCell>Total</TotalCell>
        </CreditMemoItemHeader>
        {draft.items.length > 0 ? (
          draft.items.map(
            (item, itemIdx) => (
              <React.Fragment key={item.itemId}>
                <CreditMemoItemRow>
                  <CodeCell>
                    {item.code}
                  </CodeCell>
                  <NameCell title={item.name}>
                    {item.name}
                  </NameCell>
                  <InvoicedQtyCell>
                    {"SERVICE" === item.type ?
                      item.invoicedQuantity :
                      getProductInvoicedQuantity(item)}
                  </InvoicedQtyCell>
                  <QuantityCell>
                    {"SERVICE" === item.type ?
                      <QuantityInput
                        value={item.quantity}
                        disabled={disabled}
                        onChange={(e) => updater.setItemQuantity(item.itemId, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onBlur={(e) => {
                          const initialItem = creditMemo.items[itemIdx];
                          if ("SERVICE" === initialItem.type && Number(item.quantity) > initialItem.invoicedQuantity) {
                            updater.setItemQuantity(item.itemId, String(initialItem.quantity));
                          }
                        }}
                      /> :
                      getProductQuantity(item)
                    }
                  </QuantityCell>
                  <UnitPriceCell>
                    {"SERVICE" === item.type ? (
                      <CostInput
                        Icon={<span>{getCurrencySymbol("en-US", creditMemo.currency)}</span>}
                        value={item.unitPrice}
                        disabled={disabled}
                        onChange={(e) => updater.setItemUnitPrice(item.itemId, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onBlur={() => {
                          const formattedMoney = formatMoneyCurrency(item.unitPrice, creditMemo.currency, 2, false);
                          updater.setItemUnitPrice(item.itemId, formattedMoney);
                        }}
                      />
                    ) : <>&nbsp;</>}
                  </UnitPriceCell>
                  <SubtotalCell>
                    {formatMoneyCurrency(("SERVICE" === item.type ? getSubtotal(item) : getProductSubtotal(item)), creditMemo.currency)}
                  </SubtotalCell>
                  <TaxCell title={("SERVICE" === item.type ? getTaxes(item) : getProductTaxes(item)).join(", ")}>
                    {("SERVICE" === item.type ? getTaxes(item) : getProductTaxes(item)).join(", ")}
                  </TaxCell>
                  <TotalCell>
                    {formatMoneyCurrency(("SERVICE" === item.type ? getTotal(item) : getProductTotal(item)), creditMemo.currency)}
                  </TotalCell>
                </CreditMemoItemRow>
                {"PRODUCT" === item.type && item.breakdowns.map(
                  (breakdown, breakdownIdx) => (
                    <CreditMemoItemRow key={breakdown.breakdownId} style={{ background: colors.primary1['20'] }}>
                      <CodeCell style={{ background: colors.white }}>
                        &nbsp;
                      </CodeCell>
                      <NameCell title={breakdown.description}>
                        {breakdown.description}
                      </NameCell>
                      <InvoicedQtyCell>
                        {breakdown.invoicedQuantity}
                      </InvoicedQtyCell>
                      <QuantityCell>
                        <QuantityInput
                          value={breakdown.quantity}
                          disabled={disabled}
                          onChange={(e) => updater.setBreakdownQuantity(item.itemId, breakdown.breakdownId, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            const initialItem = creditMemo.items[itemIdx];
                            if ("PRODUCT" === initialItem.type && Number(breakdown.quantity) > initialItem.breakdowns[breakdownIdx].invoicedQuantity) {
                              updater.setBreakdownQuantity(item.itemId, breakdown.breakdownId, String(initialItem.breakdowns[breakdownIdx].quantity));
                            }
                          }}
                        />
                      </QuantityCell>
                      <UnitPriceCell>
                        <CostInput
                          Icon={<span>{getCurrencySymbol("en-US", creditMemo.currency)}</span>}
                          value={breakdown.unitPrice}
                          disabled={disabled}
                          onChange={(e) => updater.setBreakdownUnitPrice(item.itemId, breakdown.breakdownId, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={() => {
                            const formattedMoney = formatMoneyCurrency(breakdown.unitPrice, creditMemo.currency, 2, false);
                            updater.setBreakdownUnitPrice(item.itemId, breakdown.breakdownId, formattedMoney);
                          }}
                        />
                      </UnitPriceCell>
                      <SubtotalCell>
                        {formatMoneyCurrency(getSubtotal(breakdown), creditMemo.currency)}
                      </SubtotalCell>
                      <TaxCell title={getTaxes(breakdown).join(", ")}>
                        {getTaxes(breakdown).join(", ")}
                      </TaxCell>
                      <TotalCell>
                        {formatMoneyCurrency(getTotal(breakdown), creditMemo.currency)}
                      </TotalCell>
                    </CreditMemoItemRow>
                  )
                )}
                {"PRODUCT" === item.type && item.costs.map(
                  (cost, costIdx) => (
                    <CreditMemoItemRow key={cost.costId} style={{ background: colors.primary1['20'] }}>
                      <CodeCell style={{ background: colors.white }}>
                        &nbsp;
                      </CodeCell>
                      <NameCell title={cost.description}>
                        {cost.description}
                      </NameCell>
                      <InvoicedQtyCell>
                        {cost.invoicedQuantity}
                      </InvoicedQtyCell>
                      <QuantityCell>
                        <QuantityInput
                          value={cost.quantity}
                          disabled={disabled}
                          onChange={(e) => updater.setCostQuantity(item.itemId, cost.costId, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            const initialItem = creditMemo.items[itemIdx];
                            if ("PRODUCT" === initialItem.type && Number(cost.quantity) > initialItem.costs[costIdx].invoicedQuantity) {
                              updater.setCostQuantity(item.itemId, cost.costId, String(initialItem.costs[costIdx].quantity));
                            }
                          }}
                        />
                      </QuantityCell>
                      <UnitPriceCell>
                        <CostInput
                          Icon={<span>{getCurrencySymbol("en-US", creditMemo.currency)}</span>}
                          value={cost.unitPrice}
                          disabled={disabled}
                          onChange={(e) => updater.setCostUnitPrice(item.itemId, cost.costId, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={() => {
                            const formattedMoney = formatMoneyCurrency(cost.unitPrice, creditMemo.currency, 2, false);
                            updater.setCostUnitPrice(item.itemId, cost.costId, formattedMoney);
                          }}
                        />
                      </UnitPriceCell>
                      <SubtotalCell>
                        {formatMoneyCurrency(getSubtotal(cost), creditMemo.currency)}
                      </SubtotalCell>
                      <TaxCell title={getTaxes(cost).join(", ")}>
                        {getTaxes(cost).join(", ")}
                      </TaxCell>
                      <TotalCell>
                        {formatMoneyCurrency(getTotal(cost), creditMemo.currency)}
                      </TotalCell>
                    </CreditMemoItemRow>
                  )
                )}
              </React.Fragment>
            )
          )
        ) : (
          <CreditMemoItemRow style={{ justifyContent: "center" }}>
            No items
          </CreditMemoItemRow>
        )}
      </InvoiceDetails>
    </FormPopup>
  );
}
